import { Typography } from '@dapp/multi-lib/sdaoui/Typography';
import { Tiny2 } from 'components/Typography';
import { useTranslation } from 'react-i18next';
import { useConnection, useSwitchChain, useWalletClient } from 'wagmi';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';
import { useSDAOLaunchpadClaim } from 'structure/claim/hooks/useSDAOLaunchpadClaim';
import { displayBalance } from 'utils/balance';
import { useUser } from 'structure/user/UserContext';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { useState } from 'react';
import { Button as BaseButton } from 'baseui/button';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import styles from '../../../migration/components/MigrationPanel/migrationpanel.module.css';
import { Spinner } from 'baseui/spinner';
import React, { ReactNode } from 'react';
import Image from 'next/image';
import { getStaticAssetUrl } from 'utils/url';
import { ConnectButton } from 'components/ConnectButton';
import { Body2 } from 'components/Typography';

type ClaimListTableRowProps = {
  item: (typeof import('utils/constants/claim.const').userClaimableList)[0];
};

interface ICell {
  children: ReactNode;
  className?: string;
}

const Cell = ({ children, className }: ICell) => {
  return (
    <td
      className={twMerge(
        'h-[72px] text-nowrap bg-[#191A1E] px-4 py-2 text-center text-sm first:rounded-l-[10px] last:rounded-r-[10px]',
        className
      )}
    >
      {children}
    </td>
  );
};

export function ClaimListTableRow({ item }: ClaimListTableRowProps) {
  const { t } = useTranslation('claim');
  const { switchChain } = useSwitchChain();
  const toastV2 = useToast();
  const csr = useClientRenderDelay();
  const { isConnected } = useConnection();
  const { chainId, account } = useUser();
  const [isDisableButton, setIsDisableButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data: walletClient } = useWalletClient();
  const {
    data: claimData,
    refetch,
    loading: isClaimDataLoading,
  } = useSDAOLaunchpadClaim(item?.projectClaimContractAddress, item.poolId);
  const checkSelectedPoolNetwork = item?.poolChainId !== chainId;
  const isClaimableAmountZero = Number(claimData?.claimableAmount) === 0;
  // const isClaimAvailable = Boolean(claimData?.userInfo?.claimed);
  const buttonDisabled =
    !checkSelectedPoolNetwork &&
    (isClaimableAmountZero || isDisableButton || !claimData);

  const handleNetworkChange = () => {
    return switchChain?.({ chainId: item?.poolChainId });
  };

  const addTokenToMetamask = async () => {
    try {
      await walletClient?.watchAsset({
        type: 'ERC20',
        options: {
          address: item?.sfiToken,
          symbol: item?.symbol,
          decimals: item?.decimals,
        },
      });
    } catch (error) {
      console.info(error);
    }
  };

  const handleClaim = async () => {
    setIsLoading(true);
    try {
      if (!claimData?.launchPadClaimcontract || !account) {
        setIsLoading(false);
        return null;
      }
      if (isClaimableAmountZero) {
        setIsLoading(false);
        toastV2({
          title: t('common:ERROR_MESSAGES.ERROR'),
          description: t(
            'common:ERROR_MESSAGES.FAILED_DUE_TO_INSUFFICIENT_TOKEN'
          ),
          type: 'error',
        });
        return;
      }
      setIsDisableButton(true);
      const txn = await claimData.claim();
      await txn.wait();
      await refetch();
      setIsLoading(false);
      setIsDisableButton(false);
      toastV2({
        title: t('common:ERROR_MESSAGES.SUCCESS'),
        description: t('common:ERROR_MESSAGES.CLAIM_SUCCESSFUL'),
        type: 'success',
      });
    } catch (error) {
      console.error('Error claiming tokens', error);
      setIsDisableButton(false);
      setIsLoading(false);
      toastV2({
        title: t('common:ERROR_MESSAGES.ERROR'),
        description: t('common:ERROR_MESSAGES.CLAIM_ERROR'),
        type: 'error',
      });
    }
  };

  return (
    <tr>
      <Cell>
        {isClaimDataLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <Typography className='pt-px text-xs text-[#B4BCD0]'>
            {displayBalance(
              claimData?.userInfo?.allocated || '0',
              item?.decimals,
              true
            ) ?? 'N/A'}{' '}
            <Tiny2 className='text-white-500'>{item.symbol}</Tiny2>
          </Typography>
        )}
      </Cell>
      <Cell>
        {isClaimDataLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <Typography className='pt-px text-xs text-[#B4BCD0]'>
            {claimData?.poolInfo?.emissionPeriod?.start ?? '---'}
          </Typography>
        )}
      </Cell>
      <Cell>
        {isClaimDataLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <Typography className='pt-px text-xs text-[#B4BCD0]'>
            {claimData?.poolInfo?.emissionPeriod?.end ?? '---'}
          </Typography>
        )}
      </Cell>
      <Cell>
        {isClaimDataLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <Typography className='pt-px text-xs text-[#B4BCD0]'>
            {displayBalance(
              claimData?.userInfo?.claimed || '0',
              item?.decimals,
              true
            ) ?? 'N/A'}{' '}
            <Tiny2 className='text-white-500'>{item.symbol}</Tiny2>
          </Typography>
        )}
      </Cell>
      <Cell>
        {isClaimDataLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <Typography className='pt-px text-xs text-[#B4BCD0]'>
            {displayBalance(
              claimData?.claimableAmount || '0',
              item?.decimals,
              true
            ) ?? 'N/A'}{' '}
            <Tiny2 className='text-lightYellow'>{item.symbol}</Tiny2>
          </Typography>
        )}
      </Cell>
      <Cell>
        <div className='flex flex-col items-center gap-2 sm:items-end'>
          {!isConnected && csr && (
            <ConnectButton className='px-4 py-1 text-sm hover:bg-white hover:text-black-1000' />
          )}
          {isConnected && csr && (
            <>
              <BaseButton
                disabled={buttonDisabled}
                isLoading={isLoading}
                className={cx(
                  twMerge(
                    styles.button,
                    'text-nowrap !p-2 !font-normal',
                    buttonDisabled && '!bg-box-borderLight !text-mono-100'
                  )
                )}
                onClick={
                  checkSelectedPoolNetwork ? handleNetworkChange : handleClaim
                }
              >
                {checkSelectedPoolNetwork
                  ? t('migrate:SWITCH_NETWORK')
                  : t('CLAIM')}
              </BaseButton>
              <button
                className='flex cursor-pointer items-center space-x-2 rounded-full text-white'
                onClick={addTokenToMetamask}
              >
                <Body2 className='hidden text-xs sm:block'>
                  {t('ADD_TO_WALLET')}
                </Body2>
                <Image
                  alt='meta-mast'
                  width={20}
                  height={20}
                  src={getStaticAssetUrl(
                    '/sfi/static/image/wallets/metamask.svg'
                  )}
                />
              </button>
            </>
          )}
        </div>
      </Cell>
    </tr>
  );
}
